import React, { useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { PublicClientApplication } from "@azure/msal-browser";
import {
  Box,
  Paper,
  TableContainer,
  Typography,
  Snackbar,
  Alert,
} from "@mui/material";
import SipocModal from "../../Admin/Users/SipocModal";
import Styles from "./FinalStatus.module.css";
import StatusTabs from "../../FinalStatusRefactor/StatusTabs";
import SipocCard from "../../FinalStatusRefactor/SipocCard";
import DeleteConfirmationDialog from "../../FinalStatusRefactor/DeleteConfirmationDialog";
import { apiService } from "../../../../api/apiService";
import { useNavigate } from "react-router-dom";
import SipocModalAdmin from "../../Admin/Users/Admin-End/SipocModalAdmin";

interface Content {
  suppliers: string;
  outputs: string;
  inputs: string;
  id: number;
  created_at: string;
  updated_at: string;
  updated_by?: string;
  created_by?: string;
  status: string;
  bd_stage?: string;
  clients: string;
  is_existing_bd?: boolean;
  doc_path?: string;
  file_name?: string;
  file_size?: string;
  management?: string;
  function_l1?: string;
  function_l2?: string;
  manager_id?: string;
  approved_by?: string;
  reason_to_approve?: string;
}

interface FinalStatusProps {
  functionL1: string;
  functionL2: string;
  management: string;
  businessDescriptionData: any;
  onSuccess?: () => void;
  onReload?: () => void;
}

const statusColors: Record<string, string> = {
  Pending: "#FF5C00",
  Draft: "#D7A833",
  Reverted: "#FF0000",
  Approved: "#058B2B",
};

const CheckBDStatus: React.FC<FinalStatusProps> = ({
  functionL1,
  functionL2,
  management,
  businessDescriptionData,
  onSuccess,
  onReload,
}) => {
  const navigate = useNavigate();
  const { instance } = useMsal();

  // State declarations
  const [tabValue, setTabValue] = useState(0);
  const [content, setContent] = useState<Content[]>([]);
  const [filteredContent, setFilteredContent] = useState<Content[]>([]);
  // console.log("filteredContent.length", filteredContent.length);

  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());
  const [selectedSipocId, setSelectedSipocId] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error",
  });

  // Handlers
  const handleEdit = (id: number) => {
    navigate(`/operations/management/edit/${id}`, {
      state: {
        functionL1,
        functionL2,
        management,
        // Add any other necessary state from the current context
        from: "edit", // Optional: helps identify navigation source
      },
      replace: false,
    });
  };

  const [deletingId, setDeletingId] = useState<number | null>(null);

  const handleClickOpen = (id: number) => {
    setDeletingId(id);
    setOpen(true);
  };

  const handleClose = () => setOpen(false);
  const handleOpenModal = (id: string) => {
    setSelectedSipocId(id);
    setIsModalOpen(true);
  };
  const handleCloseModal = () => setIsModalOpen(false);

  const processBusinessData = (data: any) => {
    if (data && data.data) {
      if (
        data.data.new_bd_list !== undefined ||
        data.data.existing_bd_list !== undefined
      ) {
        const newBDList = Array.isArray(data.data.new_bd_list)
          ? data.data.new_bd_list
          : data.data.new_bd_list
            ? [data.data.new_bd_list]
            : [];

        const existingBDList = Array.isArray(data.data.existing_bd_list)
          ? data.data.existing_bd_list
          : data.data.existing_bd_list
            ? [data.data.existing_bd_list]
            : [];

        const combinedData = [...newBDList, ...existingBDList];

        setContent(combinedData);
        setFilteredContent(combinedData);
      }
      // else {
      //   const dataArray = Array.isArray(data.data) ? data.data : [data.data];
      //   console.log("Data Array (Original Structure):", dataArray);
      //   setContent(dataArray);
      //   setFilteredContent(dataArray);
      // }
      setLastUpdate(new Date());
    }
  };

  console.log('filteredContent', filteredContent);

  // Data initialization
  useEffect(() => {
    processBusinessData(businessDescriptionData);
  }, [businessDescriptionData]);

  const handleDelete = async () => {
    if (deletingId === null) return;

    setIsLoading(true);
    try {

      const itemToDelete = content.find(item => item.id === deletingId);

      const endpoint = itemToDelete?.is_existing_bd
        ? `/management/existing_business_desc/${deletingId}`
        : `/management/business_description/${deletingId}`;

      const response = await apiService(
        endpoint,
        "DELETE",
        undefined,
        undefined,
        instance as PublicClientApplication
      );


      setSnackbar({
        open: true,
        message: "Item deleted successfully",
        severity: "success",
      });

      if (onSuccess) {
        onSuccess();
        processBusinessData(businessDescriptionData);
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Failed to delete item",
        severity: "error",
      });
    } finally {
      setIsLoading(false);
      setDeletingId(null);
      handleClose();
    }
  };

  // Tab handling
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    const statuses = ["All", "Pending", "Approved", "Reverted", "Draft"];
    const selectedStatus = statuses[newValue];
    setFilteredContent(
      selectedStatus === "All"
        ? content
        : content.filter((item) => item.status === selectedStatus)
    );
  };

  // Tab counts calculation
  const getTabCounts = (content: Content[]) => {
    return {
      All: content.length,
      Pending: content.filter((item) => item.status === "Pending").length,
      Approved: content.filter((item) => item.status === "Approved").length,
      Reverted: content.filter((item) => item.status === "Reverted").length,
      Draft: content.filter((item) => item.status === "Draft").length,
    };
  };

  const tabLabels = ["All", "Pending Queue", "Approved", "Reverted", "Draft"];
  const tabCounts = getTabCounts(content);

  return (
    <div className={Styles.mainContent}>
      <Paper
        elevation={0}
        className={Styles.createNewBox}
        sx={{ p: 0, height: "100%" }}
      >
        <Paper sx={{ p: 2 }}>
          <StatusTabs
            tabValue={tabValue}
            handleTabChange={handleTabChange}
            tabLabels={tabLabels}
            tabCounts={tabCounts}
          />

          <TableContainer component={Paper} sx={{ mt: 2, boxShadow: "none" }}>
            <div className={Styles.FinalLists}>
              {filteredContent.length > 0 ? (
                filteredContent.map((row) => (
                  <SipocCard
                    key={row.id}
                    row={row}
                    handleDelete={handleDelete}
                    handleEdit={handleEdit}
                    handleOpenModal={handleOpenModal}
                    handleClickOpen={handleClickOpen}
                    statusColors={statusColors}
                    onSuccess={onSuccess}
                  />
                ))
              ) : (
                <Typography
                  variant="body1"
                  sx={{ textAlign: "center", mt: 4, color: "text.secondary" }}
                >
                  No data available for this tab.
                </Typography>
              )}
            </div>
          </TableContainer>
          {/* <Typography
            variant="body2"
            sx={{
              mt: 2,
              color: "text.secondary",
              fontSize: "14px",
              fontWeight: "600",
            }}
          >
            Last Update: {lastUpdate.toLocaleString()}
          </Typography> */}
        </Paper>
      </Paper>

      {localStorage.getItem("role") === "manager" && (
        <SipocModal
          open={isModalOpen}
          onClose={handleCloseModal}
          sipocId={selectedSipocId}
          functionL1={functionL1}
          functionL2={functionL2}
        />
      )}

      {(localStorage.getItem("role") === "admin" ||
        localStorage.getItem("role") === "super_admin") && (
          <SipocModalAdmin
            open={isModalOpen}
            onClose={handleCloseModal}
            sipocId={selectedSipocId}
            functionL1={functionL1}
            functionL2={functionL2}
          />
        )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <DeleteConfirmationDialog
        open={open}
        handleClose={handleClose}
        handleDelete={handleDelete}
        row={content.find((item) => item.id === deletingId)}
        loading={isLoading}
      />
    </div>
  );
};

export default CheckBDStatus;
